import { StyleSpecification } from 'maplibre-gl';

import { toCountryCode } from './countries';

/**
 * Fournisseur d'imagerie satellite pour un pays.
 *
 * <p>Chaque pays qui publie son orthophotographie a la sienne, bien plus précise qu'une couverture
 * mondiale — mais son service ne répond que sur son territoire. L'IGN interrogé sur Prague ne rend
 * rien, d'où la carte blanche qu'on observait.
 */
export interface SatelliteProvider {
  /** Identifiant stable, utile aux tests et au journal. */
  id: string;
  /** Nom affiché quand on veut dire d'où vient l'image. */
  label: string;
  /** Mention légale exigée par le fournisseur. */
  attribution: string;
  /** Une spécification complète, ou l'URL d'un style hébergé — MapLibre accepte les deux. */
  style: StyleSpecification | string;
}

/** Codes ISO 3166-1 alpha-2 des pays disposant d'un fournisseur dédié. */
export type SatelliteCountry = 'FR' | 'CZ';

/** Faute de pays reconnu, c'est la France — le parc est très majoritairement français. */
export const DEFAULT_SATELLITE_COUNTRY: SatelliteCountry = 'FR';

const IGN_ATTRIBUTION = '© IGN / Géoplateforme';
const CUZK_ATTRIBUTION = '© ČÚZK';
const MAPTILER_ATTRIBUTION = '© MapTiler © OpenStreetMap contributors';

/**
 * France — orthophotographie de l'IGN, servie en WMS.
 *
 * <p>{@code maxzoom} est volontairement posé : au-delà, le service renvoie des dalles vides plutôt
 * qu'une erreur, et MapLibre afficherait du blanc sans qu'on comprenne pourquoi. Le plafonner fait
 * agrandir la dernière dalle disponible, ce qui reste lisible.
 */
const FRANCE: SatelliteProvider = {
  id: 'ign-fr',
  label: 'IGN — Ortho Express',
  attribution: IGN_ATTRIBUTION,
  style: {
    version: 8,
    name: 'IGN Ortho Express',
    glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
    sources: {
      satellite: {
        type: 'raster',
        tiles: [
          'https://data.geopf.fr/wms-r/ows?bbox={bbox-epsg-3857}&crs=EPSG%3A3857&request=GetMap' +
            '&service=WMS&height=512&layers=ORTHOIMAGERY.ORTHOPHOTOS.ORTHO-EXPRESS.2024' +
            '&version=1.3.0&width=512&format=image%2Fpng&styles=',
        ],
        tileSize: 512,
        maxzoom: 17,
        attribution: IGN_ATTRIBUTION,
      },
    },
    layers: [{ id: 'raster_satellite', type: 'raster', source: 'satellite', paint: {} }],
  },
};

/**
 * Tchéquie — orthophotographie du ČÚZK, en tuiles XYZ.
 *
 * <p>Le gabarit vient d'un service ArcGIS, dont l'ordre est {@code tile/{niveau}/{ligne}/{colonne}}
 * — soit {@code {z}/{y}/{x}}. MapLibre substitue les jetons où qu'ils soient, donc l'URL telle
 * quelle est correcte ; l'inverser donnerait une image décalée sans rien signaler.
 */
const CZECHIA: SatelliteProvider = {
  id: 'cuzk-cz',
  label: 'ČÚZK — Ortofoto',
  attribution: CUZK_ATTRIBUTION,
  style: {
    version: 8,
    name: 'ČÚZK Ortofoto',
    glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
    sources: {
      satellite: {
        type: 'raster',
        tiles: ['https://ags.cuzk.gov.cz/arcgis1/rest/services/ORTOFOTO_WM/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        maxzoom: 19,
        attribution: CUZK_ATTRIBUTION,
      },
    },
    layers: [{ id: 'raster_satellite', type: 'raster', source: 'satellite', paint: {} }],
  },
};

/**
 * Reste du monde — MapTiler, dont la clé est déjà en service pour le fond de plan.
 *
 * <p>Moins précis qu'une orthophotographie nationale, mais couvrant partout : mieux vaut une image
 * grossière qu'une carte blanche. C'est aussi la voie licenciée — Esri World Imagery, le réflexe
 * habituel, n'autorise pas clairement l'intégration hors ArcGIS dans un produit commercial.
 */
const worldwide = (apiKey: string): SatelliteProvider => ({
  id: 'maptiler-world',
  label: 'MapTiler Satellite',
  attribution: MAPTILER_ATTRIBUTION,
  style: `https://api.maptiler.com/maps/satellite/style.json?key=${apiKey}`,
});

const BY_COUNTRY: Record<SatelliteCountry, SatelliteProvider> = {
  FR: FRANCE,
  CZ: CZECHIA,
};

/**
 * Le pays a-t-il un fournisseur dédié ?
 *
 * <p>La reconnaissance du libellé est déléguée à {@link toCountryCode}, la même table que celle
 * qui alimente la liste déroulante du formulaire — une seule correspondance à maintenir.
 */
export function countryCodeOf(country: string | null | undefined): SatelliteCountry | null {
  const code = toCountryCode(country);
  return code && code in BY_COUNTRY ? (code as SatelliteCountry) : null;
}

/**
 * Le fournisseur satellite d'un site, d'après le pays de son adresse.
 *
 * @param country libellé libre issu de {@code Address.country}
 * @param apiKey  clé MapTiler, pour la couverture mondiale
 */
export function resolveSatellite(country: string | null | undefined, apiKey: string): SatelliteProvider {
  const code = countryCodeOf(country);
  if (code) {
    return BY_COUNTRY[code];
  }
  // Adresse sans pays : la France par défaut, c'est là qu'est l'essentiel du parc et c'est le
  // comportement actuel. Un pays renseigné mais sans fournisseur dédié bascule sur la couverture
  // mondiale : afficher l'IGN sur Berlin ne donnerait qu'une carte blanche.
  return country?.trim() ? worldwide(apiKey) : BY_COUNTRY[DEFAULT_SATELLITE_COUNTRY];
}
